/* eslint-disable jsx-a11y/media-has-caption */
import clsx from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import useStyles from './VideoComponent.styles'
import { VideoComponentProps } from './VideoComponentProps'
import { BrightCovePlayer } from './BrightCovePlayer'
import { VideoPlayerContext } from './VideoPlayerContext'
import VideoOverlay from './VideoOverlay/VideoOverlay'
import { trailerPlayed } from '../../utils/analytics'
import ComponentType from '../../../data/types/components/ComponentType'

declare global {
  interface Window {
    videojs: any
    bc(element: HTMLElement): BrightCovePlayer
  }
}

const VideoPlayer: React.FC<VideoComponentProps> = ({
  video,
  autoplay,
  comment = ''
}: VideoComponentProps) => {
  const classes = useStyles()
  const videoElem = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [player, setPlayer] = useState<BrightCovePlayer | null>(null)

  useEffect(() => {
    if (!videoElem.current) {
      return
    }

    setPlayer(window.bc(videoElem.current!))
  }, [videoElem.current])

  const onPlayed = useCallback(() => {
    setIsPlaying(true)
    trailerPlayed(`${ComponentType.Video} "${comment}"`)
  }, [setIsPlaying])

  const onPaused = useCallback(() => {
    setIsPlaying(false)
  }, [setIsPlaying])

  const onFullscreenChanged = useCallback(() => {
    setIsFullscreen(player?.isFullscreen() ?? false)
  }, [player, setIsFullscreen])

  // Handles the player’s lifecycle and event binding
  useEffect(() => {
    if (!player) {
      return undefined
    }

    const videoProcessed = player.catalog.transformVideoResponse(video)
    // load content to player
    player.catalog.load(videoProcessed)
    player.on('play', onPlayed)
    player.on('pause', onPaused)
    player.on('fullscreenchange', onFullscreenChanged)
    return () => {
      player.off('play', onPlayed)
      player.off('pause', onPaused)
      player.off('fullscreenchange', onFullscreenChanged)
    }
  }, [player])

  // Trigger autoplay when the video first time enters the viewport
  // Pause the video every time when exiting viewport 
  useEffect(() => {
    const el = videoElem.current 
    if (!player || !el) {
      return undefined // fix esLint consistent-return
    }

    let hasAutoplayed = false

    const observer = new IntersectionObserver(
      (entries) => {
        // Only observe one element
        const [entry] = entries
        if (!entry) return

        if (entry.isIntersecting) {
          if (!hasAutoplayed) {
            // Most browsers only allow autoplay when the video is muted
            player.muted(true)
            player.autoplay(!!autoplay)
            hasAutoplayed = true
          }
        } else if (hasAutoplayed) {
          player.pause()
        }
      },
      {
        root: null,
        threshold: 0.5 // Fires when the video element crosses 50% visibility
      }
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [player])

  // Fix the progress bar click ratio
  useEffect(() => {
    if (!player) return

    player.ready(() => {
      const seekBar: any = player.controlBar?.progressControl?.seekBar
      /* seekBar.calculateDistance is Brightcove internal function that 
      ** converts a mouse click into a ratio [0..1] along the progress bar.
      ** Rebinding ensures that the computation of the ratio is correct.
      */
      const original = seekBar.calculateDistance.bind(seekBar)
      // Cast seekBarElement to HTMLElement, 
      // because the getBoundingClientRect() is a method on DOM elements only
      const seekBarElement = seekBar.el() as HTMLElement
      // Track the last valid distance to handle document-level events
      let previousDistance = 0
      seekBar.calculateDistance = (event: MouseEvent) => {
        // Always trigger layout reflow to keep progress ratio accurate
        seekBarElement.getBoundingClientRect()
        const distance = original(event)
        // When currentTarget is a real DOM element, use the new distance
        // When it's document, reuse the previous distance to prevent jump
        if (event.currentTarget instanceof HTMLElement) {
          previousDistance = distance
          return distance
        }

        return previousDistance
      }
    })
  }, [player])

  // Replace remaining time with "current time / duration"
  useEffect(() => {
    if (!player) return

    player.ready(() => {
      const { controlBar } = player
      if (!controlBar) return

      const remainingDisplay = controlBar.getChild('remainingTimeDisplay') as any
      if (!remainingDisplay) return

      // Hide the existing remaining time element in the DOM
      const remainingTimeEl = controlBar.el().querySelector('.vjs-remaining-time') as HTMLElement
      if (remainingTimeEl) {
        remainingTimeEl.style.display = 'none'
      }

      /* 
      ** Override Brightcove internal RemainingTimeDisplay.updateContent()
      ** to display "current time / duration" instead of remaining time.
      ** This ensures updates stay in sync with Brightcove’s UI refresh cycle.
      */ 
      remainingDisplay.updateContent = () => {
        const durationRaw = player.duration()
        if (!durationRaw || Number.isNaN(durationRaw)) return
        const durationTime = Math.floor(durationRaw)
        const currentTime = Math.floor(player.currentTime())

        // Update time format, for example: 104 -> 1:44
        const current = window.videojs.formatTime(currentTime)
        const duration = window.videojs.formatTime(durationTime)
        
        // Create a custom time element, for example: "1:02 / 1:44"
        let timeDisplayEl = controlBar.el().querySelector('.vjs-custom-time-display')
        if (!timeDisplayEl) {
          timeDisplayEl = document.createElement('div')
          timeDisplayEl.className = 'vjs-custom-time-display vjs-time-control vjs-control'
          controlBar.el().insertBefore(timeDisplayEl, remainingTimeEl)
        }
        timeDisplayEl.textContent = `${current} / ${duration}`
      }

      remainingDisplay.updateContent()
    })
  }, [player])

  return (
    <div className={clsx(classes.video)}>
      <div className={clsx(classes.videoRatio)}>
        <div className={clsx(classes.videoContainer)}>
          {/* multiple videos with same id might interfere one another */}
          <video
            id={'player'.concat(Math.random().toString(16).slice(2))}
            className={clsx('video-js vjs-fill', classes.videoPlayer)}
            ref={videoElem}
            controls
          />
          {player &&
            ReactDOM.createPortal(
              <VideoPlayerContext.Provider
                value={{ player, isPlaying, isFullscreen }}
              >
                <VideoOverlay />
              </VideoPlayerContext.Provider>,
              player.el()
            )}
        </div>
      </div>
    </div>
  )
}

export default VideoPlayer
